import { Component, OnInit, OnDestroy, ViewChild, WritableSignal, signal } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService, ConfirmationService, MenuItem } from 'primeng/api';
import { BreadcrumbService } from '../../app.breadcrumb.service';

import { VenteService } from '../../services/vente-service';
import { VenteEntite } from '../../entites/vente';
import { ClientEntite } from '../../entites/client';
import { EmployeEntite } from '../../entites/employe';
import { Statut } from '../../entites/statut';
import { BaseComponent } from 'src/app/base/base-component';
import { DonneesBaseService } from 'src/app/services/donnees-base-service';
import { ProfilService } from 'src/app/services/profil-service';
import { AppMainComponent } from 'src/app/app.main.component';
import { EmployeService } from 'src/app/services/employe-service';
import { FournisseurService } from 'src/app/services/fournisseur-service';
import { AchatService } from 'src/app/services/achat-service';

/**
 * @component DetailVenteComponent
 * @description Component for viewing and editing vente (sale) details.
 * Handles both creation of new ventes and modification of existing ones.
 */
@Component({
    templateUrl: './detail-vente.component.html',
    styleUrls: ['./detail-vente.component.scss'],
    providers: [MessageService, ConfirmationService]
})
export class DetailVenteComponent extends BaseComponent implements OnInit, OnDestroy {

    venteForm: FormGroup;
    vente: VenteEntite = {};
    venteId: number;
    isNewVente: boolean = true;
    isSaving: boolean = false;

    // Data for dropdowns
    clients: string[] = [];
    employes: EmployeEntite[] = [];
    statuts: Statut[] = [];

    menu: WritableSignal<MenuItem[]> = signal([]);

    constructor(
        sdb: DonneesBaseService,
        profilService: ProfilService,
        route: ActivatedRoute,
        appMain: AppMainComponent,
        private location: Location,
        private router: Router,
        private formBuilder: FormBuilder,
        private venteService: VenteService,
        private employeService: EmployeService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private breadcrumbService: BreadcrumbService,
        private fournisseurService: FournisseurService,
        private achatService: AchatService
    ) {
        super(sdb, profilService, route, router, appMain);
        this.initializeForm();
    }

    ngOnInit() {
        this.venteId = +this.route.snapshot.paramMap.get('id');
        this.isNewVente = this.venteId === 0;

        if (this.isNewVente) {
            this.verifierAccesPageAuMoisUnAcces(['ventes_creer']);
            this.breadcrumbService.setItems([
                { label: 'Achats', routerLink: ['/achats'] },
                { label: 'Ventes', routerLink: ['/ventes'] },
                { label: 'Nouvelle vente' }
            ]);
            this.Modifier();
        } else {
            this.verifierAccesPageAuMoisUnAcces(['ventes_modifier']);
            this.breadcrumbService.setItems([
                { label: 'Achats', routerLink: ['/achats'] },
                { label: 'Ventes', routerLink: ['/ventes'] },
                { label: 'Détail de la vente' }
            ]);
        }

        this.loadInitialData();
    }

    ngOnDestroy() {
        // Clean up any subscriptions if needed
    }

    /**
     * Initializes the reactive form for vente data
     */
    private initializeForm() {
        this.venteForm = this.formBuilder.group({
            description: ['', [Validators.required, Validators.maxLength(500)]],
            client: [null, Validators.required],
            employeId: [null, Validators.required],
            dateLivraison: [null],
            statutId: [1]
        });
    }

    /**
     * Loads initial data including clients, employees, and vente details if editing
     */
    private async loadInitialData() {
        this.DemarrerChargement();

        try {
            // Load reference data
            await this.loadReferenceData();

            // If editing existing vente, load its data
            if (!this.isNewVente) {
                await this.loadVenteData();
            }
        } catch (error) {
            this.messageService.add({
                severity: 'error',
                summary: 'Erreur',
                detail: 'Erreur lors du chargement des données'
            });
        } finally {
            this.CompleterChargement();
        }
    }

    /**
     * Loads reference data (clients, employees, statuses)
     */
    private async loadReferenceData() {
        const promises = [
            this.loadEmployes(),
            this.loadStatuts(),
            this.loadClients()
        ];

        await Promise.all(promises);
    }

    /**
     * Loads the list of employees
     */
    private loadEmployes() {
        try {
            this.employes = this.sdb.Employes();
        } catch (error) {
            console.error('Error loading employees:', error);
        }
    }

    /**
     * Loads the list of available statuses
     */
    private loadStatuts() {
        this.statuts = this.sdb.Statuts();
    }

    private loadClients() {
        this.venteService.ObtenirClients().subscribe(response => {
            if (response.estUnSucces) {
                this.clients = response.valeur;
            } else {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erreur',
                    detail: 'Erreur lors du chargement des clients'
                });
            }
        });
    }

    /**
     * Loads vente data for editing
     */
    private async loadVenteData() {
        try {
            const response = await this.venteService.ObtenirVente(this.venteId);
            if (response.estUnSucces) {
                this.vente = response.valeur;
                this.populateForm();
            } else {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erreur',
                    detail: 'Vente non trouvée'
                });
                this.router.navigate(['/ventes']);
            }
        } catch (error) {
            this.messageService.add({
                severity: 'error',
                summary: 'Erreur',
                detail: 'Erreur lors du chargement de la vente'
            });
            this.router.navigate(['/ventes']);
        }
    }

    /**
     * Populates the form with existing vente data
     */
    private populateForm() {
        this.venteForm.patchValue({
            description: this.vente.description,
            client: this.vente.client,
            employeId: this.vente.employeId,
            dateLivraison: this.vente.dateLivraison ? new Date(this.vente.dateLivraison) : null,
            statutId: this.vente.statut?.statutId || 1
        });
    }

    /**
     * Saves the vente (create or update)
     */
    async sauvegarder(event: () => void) {

        if (this.venteForm.invalid) {
            this.markFormGroupTouched(this.venteForm);
            this.messageService.add({
                severity: 'warn',
                summary: 'Attention',
                detail: 'Veuillez corriger les erreurs dans le formulaire'
            });
            return;
        }

        this.isSaving = true;

        try {
            const formValue = this.venteForm.value;
            const venteData: VenteEntite = {
                ...this.vente,
                description: formValue.description,
                client: formValue.client,
                employeId: formValue.employeId,
                dateLivraison: formValue.dateLivraison,
                statut: this.statuts.find(s => s.statutId === formValue.statutId)
            };

            let response;
            if (this.isNewVente) {
                response = await this.venteService.CreerVente(venteData);
                if (response.estUnSucces) {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Succès',
                        detail: 'Vente créée avec succès'
                    });
                    this.router.navigate(['/ventes']);
                }
            } else {
                response = await this.venteService.ModifierVente(venteData);
                if (response.estUnSucces) {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Succès',
                        detail: 'Vente modifiée avec succès'
                    });
                }
            }

            if (!response.estUnSucces) {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erreur',
                    detail: response.messages?.[0]?.description || 'Erreur lors de la sauvegarde'
                });
            }

            event();
            this.vente = response.valeur;
            this.Consulter();
        } catch (error) {
            this.messageService.add({
                severity: 'error',
                summary: 'Erreur',
                detail: 'Erreur lors de la sauvegarde de la vente'
            });
            event();
        } finally {
            this.isSaving = false;
        }
    }

    /**
     * Cancels the current operation and navigates back
     */
    annuler() {
        this.Consulter();
        this.loadInitialData();
    }

    /**
     * Marks all form controls as touched to trigger validation display
     */
    private markFormGroupTouched(formGroup: FormGroup) {
        Object.keys(formGroup.controls).forEach(key => {
            const control = formGroup.get(key);
            control.markAsTouched();
        });
    }

    /**
     * Checks if the current user can edit the vente
     */
    get canEdit(): boolean {
        return this.isNewVente ? 
            this.estAutorise('ventes_creer') : 
            this.estAutorise('ventes_modifier');
    }

    /**
     * Gets the display name for an employee
     */
    getEmployeDisplayName(employe: EmployeEntite): string {
        return employe ? `${employe.prenom} ${employe.nom}` : '';
    }

    /**
     * Gets the display name for a client
     */
    getClientDisplayName(client: ClientEntite): string {
        return client ? client.nom : '';
    }

    filterClient(event: any) {
        this.clients = this.clients.filter(client => client.toLowerCase().includes(event.query.toLowerCase()));
    }
}
